import { useState, useEffect, useRef, useCallback } from 'react';
import { SOCKET_URLS, getAreaFromGroupId, AREA_TOKEN_TO_LOCATION, LOCATION_TO_AREA_TOKEN, BOOM_BARRIER_SOCKET_URL } from '../utils/constants';
import { updateGlobalCameraData } from '../utils/cameraService';
import { VEHICLE_ALERT_SOCKET } from '../utils/constants';
import { getLoggedInArea, isAdminUser } from '../utils/authService';
import { isOffAreaGeofence, getAreaFromGeofenceId } from '../utils/constants';

const SERVER_URLS = {
  1: 'https://202.140.131.110:8888',
  2: 'https://202.140.131.118:8888',
  3: 'https://202.140.131.98:8888',
  4: 'https://202.140.131.67:8888',
  5: 'https://202.140.131.90:8888',
  6: 'https://202.140.131.82:8888',
  7: 'https://202.140.131.74:8888',
  9: 'https://202.140.131.102:8888',
  10: 'https://202.140.131.122:8888',
  11: 'https://202.140.131.114:8888',
  12: 'https://202.140.131.126:8888',
  13: 'https://202.140.131.86:8888',
  14: 'https://202.140.131.94:8888',
};

const useWebSockets = () => {
  const [connectionStatus, setConnectionStatus] = useState({});
  const [cameraLocations, setCameraLocations] = useState({});

  const [camerasData, setCamerasData] = useState({});
  const [camerasByIp, setCamerasByIp] = useState({});
  const [camerasByGroupId, setCamerasByGroupId] = useState({});

  const [crowdData, setCrowdData] = useState([]);
  const [vehicleData, setVehicleData] = useState([]);
  const [personData, setPersonData] = useState([]);
  const [congestionData, setCongestionData] = useState([]);
  const [loadedStateData, setLoadedStateData] = useState([]);
  const [unloadedStateData, setUnloadedStateData] = useState([]);

  const [offRouteData, setOffRouteData] = useState([]);
  const [tamperData, setTamperData] = useState([]);
  const [offAreaData, setOffAreaData] = useState([]);

  const [overSpeedData, setOverSpeedData] = useState([]);
  const [unauthorizedStoppageData, setUnauthorizedStoppageData] = useState([]);

  const [cameraTamperingData, setCameraTamperingData] = useState([]);
  const [safetyHazardData, setSafetyHazardData] = useState([]);
  const [insufficientilluminationData, setInsufficientilluminationData] = useState([]);

  const [isPaused, setIsPaused] = useState(false);
  const [pausedTime, setPausedTime] = useState(null);

  const [boomBarrierData, setBoomBarrierData] = useState([]);
  const boomBarrierSocketRef = useRef(null);
  const vtsLastMessageRef = useRef(Date.now()); // tracks last VTS message time for staleness detection
  const boomLastMessageRef = useRef(Date.now()); // tracks last Boom Barrier message time for staleness detection

  const [bufferedEvents, setBufferedEvents] = useState({
    cd: [],
    vd: [],
    pd: [],
    vc: [],
    ls: [],
    us: [],
    or: [],
    ta: [],
    os: [],
    usp: [],
    sh: [],
    ii: [],
    oa: []
  });

  const convertGMTtoIST = useCallback((gmtTimeString) => {
    try {

      const gmtDate = new Date(gmtTimeString);
      const istDate = new Date(gmtDate.getTime() + (5.5 * 60 * 60 * 1000));
      return istDate.toLocaleString('en-IN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch (error) {
      console.error('Error converting GMT to IST:', error);
      return gmtTimeString;
    }
  }, []);

  const socketRefs = useRef({});
  const pauseStateRef = useRef(isPaused);
  const bufferedEventsRef = useRef(bufferedEvents);

  const processedEventIds = useRef(new Map());
  const eventCounter = useRef(0);


  const eventPriorityMap = useRef(new Map());

  const cameraCache = useRef(new Map());

  useEffect(() => {
    pauseStateRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    bufferedEventsRef.current = bufferedEvents;
  }, [bufferedEvents]);

  const processCameraData = useCallback((cameras) => {

    if (!cameras || !Array.isArray(cameras) || cameras.length === 0) {
      console.warn('⚠️ No valid camera data to process');
      return;
    }


    const newCamerasData = {};
    const newCamerasByIp = {};
    const newCamerasByGroupId = {};
    const newCameraLocations = {};

    cameras.forEach(camera => {
      if (!camera || !camera.id || !camera.ip) {
        console.warn('⚠️ Invalid camera data:', camera);
        return;
      }

      const normalizedIp = camera.ip.toString().trim();

      const groupId = camera.groupId ? parseInt(camera.groupId) : null;

      const latitude = parseFloat(camera.latitude);
      const longitude = parseFloat(camera.longitude);

      const hasValidCoords = !isNaN(latitude) && !isNaN(longitude) &&
        isFinite(latitude) && isFinite(longitude) &&
        latitude >= -90 && latitude <= 90 &&
        longitude >= -180 && longitude <= 180;

      const enhancedCamera = {
        ...camera,
        ip: normalizedIp,
        groupId: groupId,
        status: camera.status || 'unknown',
        latitude: hasValidCoords ? latitude : null,
        longitude: hasValidCoords ? longitude : null,
        hasValidCoords: hasValidCoords,
        formattedLatitude: hasValidCoords ? latitude.toFixed(6) : 'N/A',
        formattedLongitude: hasValidCoords ? longitude.toFixed(6) : 'N/A',
        normalizedIp: normalizedIp,
        originalIp: camera.ip,
        processedAt: Date.now()
      };

      cameraCache.current.set(camera.id, enhancedCamera);
      cameraCache.current.set(normalizedIp, enhancedCamera);
      cameraCache.current.set(`ip_${normalizedIp}`, enhancedCamera);

      if (camera.ip !== normalizedIp) {
        cameraCache.current.set(camera.ip, enhancedCamera);
        cameraCache.current.set(`ip_${camera.ip}`, enhancedCamera);
      }

      newCamerasData[camera.id] = enhancedCamera;


      newCamerasByIp[normalizedIp] = enhancedCamera;


      if (camera.ip !== normalizedIp) {
        newCamerasByIp[camera.ip] = enhancedCamera;
      }

      if (groupId) {
        if (!newCamerasByGroupId[groupId]) {
          newCamerasByGroupId[groupId] = [];
        }
        newCamerasByGroupId[groupId].push(enhancedCamera);
      }

      if (camera.id && camera.location) {
        newCameraLocations[camera.id] = camera.location;
      }
    });

    setCamerasData(prev => {
      const merged = { ...prev, ...newCamerasData };
      return merged;
    });

    setCamerasByIp(prev => {
      const merged = { ...prev, ...newCamerasByIp };
      return merged;
    });

    setCamerasByGroupId(prev => {
      const merged = { ...prev, ...newCamerasByGroupId };
      return merged;
    });

    setCameraLocations(prev => ({ ...prev, ...newCameraLocations }));

    updateGlobalCameraData(cameras);

  }, []);

  const getCameraByIp = useCallback((ip) => {
    if (!ip) return null;

    const normalizedIp = ip.toString().trim();

    let camera = cameraCache.current.get(normalizedIp) ||
      cameraCache.current.get(`ip_${normalizedIp}`) ||
      cameraCache.current.get(ip) ||
      cameraCache.current.get(`ip_${ip}`);

    if (!camera) {
      camera = camerasByIp[normalizedIp];

      if (!camera && ip !== normalizedIp) {
        camera = camerasByIp[ip];
      }
    }

    if (camera && !cameraCache.current.has(normalizedIp)) {
      cameraCache.current.set(normalizedIp, camera);
      cameraCache.current.set(`ip_${normalizedIp}`, camera);
      if (camera.id) {
        cameraCache.current.set(camera.id, camera);
      }
    }

    if (!camera) {

    } else {

    }

    return camera || null;
  }, [camerasByIp]);

  const getCameraById = useCallback((id) => {
    return camerasData[id] || null;
  }, [camerasData]);

  const getCamerasByGroupId = useCallback((groupId) => {
    const numericGroupId = parseInt(groupId) || groupId;
    return camerasByGroupId[numericGroupId] || [];
  }, [camerasByGroupId]);

  const getVideoStreamUrl = useCallback((cameraIp, groupId) => {

    const camera = getCameraByIp(cameraIp);

    if (!camera) {
      return null;
    }
    const actualGroupId = camera.groupId || groupId;

    if (!actualGroupId) {
      console.warn(`⚠️ WebSocket Hook - No group ID available for camera: ${cameraIp}`);
      return null;
    }

    const numericGroupId = parseInt(actualGroupId);
    const serverUrl = SERVER_URLS[numericGroupId];

    if (!serverUrl) {
      console.warn(`⚠️ WebSocket Hook - No server URL found for group ID: ${numericGroupId}`);
      return null;
    }

    const streamUrl = `${serverUrl}/${camera.id}/`;

    return streamUrl;
  }, [getCameraByIp, camerasByIp]);

  const getVideoStreamUrlByEvent = useCallback((event) => {
    if (!event.cameraIp) {
      console.warn('⚠️ WebSocket Hook - No camera IP in event:', event);
      return null;
    }

    const groupId = event.groupId || event.originalMessage?.groupId;

    return getVideoStreamUrl(event.cameraIp, groupId);
  }, [getVideoStreamUrl]);

  const isCameraOnline = useCallback((cameraIp) => {
    const camera = getCameraByIp(cameraIp);
    return camera ? camera.status === 'online' : false;
  }, [getCameraByIp]);

  const getAllCamerasWithStreams = useCallback(() => {
    return Object.values(camerasData).map(camera => ({
      ...camera,
      streamUrl: getVideoStreamUrl(camera.ip, camera.groupId),
      isOnline: camera.status === 'online'
    }));
  }, [camerasData, getVideoStreamUrl]);

  const getCameraStats = useCallback(() => {
    const cameras = Object.values(camerasData);

    if (cameras.length === 0) {
      return null;
    }

    // Calculate totals
    const totalCameras = cameras.length;
    const onlineCameras = cameras.filter(c => c.status === 'online').length;
    const offlineCameras = totalCameras - onlineCameras;

    // Calculate area-wise stats
    const areaStats = {};
    cameras.forEach(camera => {
      const area = camera.area || 'Unknown';
      if (!areaStats[area]) {
        areaStats[area] = { total: 0, online: 0, offline: 0 };
      }
      areaStats[area].total++;
      if (camera.status === 'online') {
        areaStats[area].online++;
      } else {
        areaStats[area].offline++;
      }
    });

    return {
      totalCameras,
      onlineCameras,
      offlineCameras,
      areaStats,
    };
  }, [camerasData]);

  const updateEventPriority = useCallback((eventId, priority, remarks) => {
    const priorityData = {
      severity: priority,
      remarks: remarks || [],
      updatedAt: Date.now()
    };

    console.log(`🔧 Updating priority for event: ${eventId} -> ${priority}`);

    eventPriorityMap.current.set(eventId, priorityData);

    const stringEventId = eventId.toString();
    if (stringEventId !== eventId) {
      eventPriorityMap.current.set(stringEventId, priorityData);
    }

    const updateEventInArray = (setterFunction, dataType) => {
      setterFunction(prevData => {
        if (!Array.isArray(prevData)) {
          console.warn(`⚠️ ${dataType} data is not an array, initializing as empty array`);
          prevData = [];
        }

        let hasUpdates = false;
        const updatedData = prevData.map(event => {
          const eventKey = event.id || event.eventId;
          const exactMatch = eventKey === eventId || eventKey?.toString() === eventId?.toString();

          if (exactMatch) {
            console.log(`✅ Updated ${dataType} event: ${eventKey} with priority ${priority}`);
            hasUpdates = true;
            return {
              ...event,
              severity: priority,
              remarks: remarks || event.remarks || [],
              priorityUpdated: true,
              priorityUpdatedAt: Date.now(),
              isLivePrioritized: true
            };
          }
          return event;
        });

        if (hasUpdates) {
          console.log(`📊 ${dataType} data updated with new priority for event: ${eventId}`);
        } else {
          console.warn(`⚠️ No matching event found in ${dataType} data for eventId: ${eventId}`);
        }

        return updatedData;
      });
    };

    updateEventInArray(setCrowdData, 'Crowd');
    updateEventInArray(setVehicleData, 'Vehicle');
    updateEventInArray(setPersonData, 'Person');
    updateEventInArray(setCongestionData, 'Congestion');
    updateEventInArray(setLoadedStateData, 'Loaded State');
    updateEventInArray(setUnloadedStateData, 'Unloaded State');
    updateEventInArray(setOffRouteData, 'Off-Route');
    updateEventInArray(setTamperData, 'Tamper');
    updateEventInArray(setCameraTamperingData, 'Camera Tampering');
    updateEventInArray(setSafetyHazardData, 'Safety Hazard');
    updateEventInArray(setInsufficientilluminationData, 'Insufficient Illumination');

    setTimeout(() => {
      setConnectionStatus(prev => ({ ...prev, lastPriorityUpdate: Date.now() }));
    }, 100);
  }, []);


  const getEventPriority = useCallback((eventId) => {
    if (!eventId) return null;

    const exactMatch = eventPriorityMap.current.get(eventId) ||
      eventPriorityMap.current.get(eventId.toString());

    if (exactMatch) {
      console.log(`🎯 Found priority for eventId: ${eventId} -> ${exactMatch.severity}`);
      return exactMatch;
    }

    return null;
  }, []);

  const debugPriorityState = useCallback((eventId) => {
    console.log('🔍 Priority Map Debug for event:', eventId);
    console.log('📊 Total entries in priority map:', eventPriorityMap.current.size);


    const allKeys = Array.from(eventPriorityMap.current.keys());

    const exactMatch = eventPriorityMap.current.get(eventId);
    const stringMatch = eventPriorityMap.current.get(eventId?.toString());

    const similarKeys = allKeys.filter(key =>
      key.includes(eventId) || eventId.includes(key)
    );

    return {
      totalEntries: eventPriorityMap.current.size,
      exactMatch,
      stringMatch,
      similarKeys,
      allKeys: allKeys.slice(0, 10)
    };
  }, []);

  const clearPriorityData = useCallback(() => {
    const sizeBefore = eventPriorityMap.current.size;
    eventPriorityMap.current.clear();

    setConnectionStatus(prev => ({
      ...prev,
      priorityCleared: Date.now()
    }));
  }, []);

  const cleanupExpiredPriorities = useCallback(() => {
    const now = Date.now();
    const maxAge = 24 * 60 * 60 * 1000;

    for (const [eventId, data] of eventPriorityMap.current.entries()) {
      if (now - data.updatedAt > maxAge) {
        eventPriorityMap.current.delete(eventId);
      }
    }
  }, []);

  const generateUniqueEventId = useCallback((cameraIp, eventType, timestamp, messageContent) => {
    eventCounter.current += 1;
    const contentHash = messageContent ?
      btoa(JSON.stringify(messageContent)).slice(0, 8) :
      Math.random().toString(36).substr(2, 8);
    return `${timestamp}_${cameraIp || 'unknown'}_${eventType?.toUpperCase() || 'EVENT'}_${contentHash}_${eventCounter.current}`;
  }, []);


  const isDuplicateEvent = useCallback((message, eventType, timestamp) => {
    const cameraIp = message.cameraIp || 'unknown';

    const timeWindow = Math.floor(timestamp / 1000);
    const primaryKey = `${cameraIp}_${eventType}_${timeWindow}`;

    const contentKey = message.eventId ||
      `${cameraIp}_${eventType}_${JSON.stringify(message.attributes || {})}_${message.severity || ''}`;

    const now = Date.now();
    const expiry = 30000;

    const primaryEntry = processedEventIds.current.get(primaryKey);
    const contentEntry = processedEventIds.current.get(contentKey);

    if ((primaryEntry && (now - primaryEntry.timestamp) < expiry) ||
      (contentEntry && (now - contentEntry.timestamp) < expiry)) {
      return true;
    }

    processedEventIds.current.set(primaryKey, { timestamp: now });
    processedEventIds.current.set(contentKey, { timestamp: now });

    if (processedEventIds.current.size > 500) {
      const entries = Array.from(processedEventIds.current.entries());
      processedEventIds.current.clear();

      entries.forEach(([key, value]) => {
        if (now - value.timestamp < expiry) {
          processedEventIds.current.set(key, value);
        }
      });
    }

    return false;
  }, []);

  const handleEventData = useCallback((eventType, newEvent) => {
    const currentPauseState = pauseStateRef.current;

    if (!newEvent || !newEvent.id || !eventType) {
      console.warn('⚠️ Invalid event data received:', { eventType, newEvent });
      return;
    }

    const existingPriority = getEventPriority(newEvent.id) ||
      getEventPriority(newEvent.eventId) ||
      getEventPriority(newEvent.id?.toString()) ||
      getEventPriority(newEvent.eventId?.toString());

    if (existingPriority) {
      newEvent.severity = existingPriority.severity;
      newEvent.remarks = existingPriority.remarks || [];
      newEvent.priorityUpdated = true;
      newEvent.priorityUpdatedAt = existingPriority.updatedAt;
      newEvent.isLivePrioritized = true;
    } else {

      newEvent.severity = newEvent.severity || "normal";
      newEvent.remarks = newEvent.remarks || [];
      newEvent.priorityUpdated = false;
      newEvent.isLivePrioritized = false;
    }

    if (currentPauseState) {
      setBufferedEvents(prev => {
        const key = eventType?.toLowerCase();
        const currentBuffered = prev || {
          cd: [], vd: [], pd: [], vc: [], ls: [], us: [], or: [], ta: [], sh: [], ii: []
        };

        if (!Object.prototype.hasOwnProperty.call(currentBuffered, key)) {
          return currentBuffered;
        }

        const existingEvents = Array.isArray(currentBuffered[key]) ? currentBuffered[key] : [];

        return {
          ...currentBuffered,
          [key]: [newEvent, ...existingEvents]
        };
      });
    } else {
      const updateEventData = (setter, dataTypeName) => {
        setter(prev => {
          const prevArray = Array.isArray(prev) ? prev : [];

          const existingIds = new Set(prevArray.map(event => event.id));
          if (existingIds.has(newEvent.id)) {
            console.warn(`🚫 Duplicate event ID at state level: ${newEvent.id}`);
            return prevArray;
          }

          console.log(`➕ Adding new ${dataTypeName} event: ${newEvent.id}${newEvent.priorityUpdated ? ' (with priority: ' + newEvent.severity + ')' : ''}`);
          return [newEvent, ...prevArray];
        });
      };

      switch (eventType?.toLowerCase()) {
        case "cd": updateEventData(setCrowdData, "Crowd"); break;
        case "vd": updateEventData(setVehicleData, "Vehicle"); break;
        case "pd": updateEventData(setPersonData, "Person/Intrusion"); break;
        case "vc": updateEventData(setCongestionData, "Congestion"); break;
        case "ls": updateEventData(setLoadedStateData, "Loaded State"); break;
        case "us": updateEventData(setUnloadedStateData, "Unloaded State"); break;
        case "or": updateEventData(setOffRouteData, "Off-Route Alert"); break;
        case "ta": updateEventData(setTamperData, "Tamper Alert"); break;
        case "oa": updateEventData(setOffAreaData, "Off-Area Alert"); break;
        case "os": updateEventData(setOverSpeedData, "Over Speed Alert"); break;
        case "usp": updateEventData(setUnauthorizedStoppageData, "Unauthorized Stoppage"); break;
        case "ct": updateEventData(setCameraTamperingData, "Camera Tampering"); break;
        case "sh": updateEventData(setSafetyHazardData, "Safety Hazard"); break;
        case "ii": updateEventData(setInsufficientilluminationData, "Insufficient Illumination"); break;
        default:
          console.warn(`Unhandled event type: ${eventType}`);
          break;
      }
    }
  }, [getEventPriority]);

  const handleVehicleAlertMessage = useCallback((event) => {
  try {
    const data = JSON.parse(event.data);

    if (data.alertType && data.vehicleNumber) {
      const timestamp = Date.now();
      const eventId = `${timestamp}_${data.vehicleNumber}_${data.alertType.toUpperCase()}_${Math.random().toString(36).substr(2, 8)}`;

      const istTime = convertGMTtoIST(data.timeStamp);

      let actualAlertType = data.alertType;
      let targetEventType = data.alertType;

      if (data.alertType === 'stoppage') {
        actualAlertType = 'stoppage';
        targetEventType = 'usp';
      } else if (data.alertType === 'overspeed') {
        actualAlertType = 'overspeed';
        targetEventType = 'os';
      } else if (data.alertType === 'off-route') {
        // ✅ KEY FIX: infer off-area vs off-route from geofenceId
        if (data.geofenceId && isOffAreaGeofence(data.geofenceId)) {
          actualAlertType = 'off-area';
          targetEventType = 'oa';
        } else {
          actualAlertType = 'off-route';
          targetEventType = 'or';
        }
      } else if (data.alertType === 'tamper') {
        actualAlertType = 'tamper';
        targetEventType = 'ta';
      }

      // Determine off-area metadata
      const isOffArea = targetEventType === 'oa';
      const offAreaLocation = isOffArea ? getAreaFromGeofenceId(data.geofenceId) : null;

      const vehicleAlertEvent = {
        id: eventId,
        eventId: eventId,
        alertType: actualAlertType,
        vehicleNumber: data.vehicleNumber,
        groupId: data.groupId,
        geofenceId: data.geofenceId || null,
        vehicleTransporter: data.vehicleTransporter,
        currentLocation: data.currentLocation,
        alertLocation: data.alertLocation,
        allocatedGeofence: data.allocatedGeofence || [],
        alertSubType: data.alertSubType || null,
        timeStamp: data.timeStamp,
        readableTime: istTime,
        timestamp: timestamp,
        coordinates: data.currentLocation ?
          `${data.currentLocation.lat.toFixed(6)}, ${data.currentLocation.lng.toFixed(6)}` : 'N/A',
        isVehicleAlert: true,
        isOffAreaAlert: isOffArea,         // ✅ correctly set
        offAreaForArea: offAreaLocation,   // ✅ e.g. "Piparwar" for geofenceId 289
        severity: "normal",
        remarks: [],
        priorityUpdated: false,
        priorityUpdatedAt: null,
        isLivePrioritized: false,
        area: getAreaFromGroupId(data.groupId),
        originalTransporter: data.vehicleTransporter,
        category: actualAlertType?.toUpperCase() || 'VEHICLE'
      };

      if (targetEventType === 'or') {
        handleEventData('or', vehicleAlertEvent);
      } else if (targetEventType === 'oa') {
        handleEventData('oa', vehicleAlertEvent);
      } else if (targetEventType === 'ta') {
        handleEventData('ta', vehicleAlertEvent);
      } else if (targetEventType === 'os') {
        handleEventData('os', vehicleAlertEvent);
      } else if (targetEventType === 'usp') {
        handleEventData('usp', vehicleAlertEvent);
      }
    }
  } catch (error) {
    console.error('❌ Error parsing vehicle alert message:', error);
  }
}, [handleEventData, convertGMTtoIST]);


  const togglePause = useCallback(() => {
    if (pauseStateRef.current) {
      resumeFromPause();
    } else {
      setPausedTime(Date.now());
    }
    setIsPaused(!pauseStateRef.current);
  }, []);

  const resumeFromPause = useCallback(() => {
    const currentBuffered = bufferedEventsRef.current;

    setCrowdData(prev => {
      const prevArray = Array.isArray(prev) ? prev : [];
      const bufferedArray = Array.isArray(currentBuffered.cd) ? currentBuffered.cd : [];
      if (bufferedArray.length === 0) return prevArray;
      return [...bufferedArray, ...prevArray];
    });

    setVehicleData(prev => {
      const prevArray = Array.isArray(prev) ? prev : [];
      const bufferedArray = Array.isArray(currentBuffered.vd) ? currentBuffered.vd : [];
      if (bufferedArray.length === 0) return prevArray;
      return [...bufferedArray, ...prevArray];
    });

    setPersonData(prev => {
      const prevArray = Array.isArray(prev) ? prev : [];
      const bufferedArray = Array.isArray(currentBuffered.pd) ? currentBuffered.pd : [];
      if (bufferedArray.length === 0) return prevArray;
      return [...bufferedArray, ...prevArray];
    });

    setCongestionData(prev => {
      const prevArray = Array.isArray(prev) ? prev : [];
      const bufferedArray = Array.isArray(currentBuffered.vc) ? currentBuffered.vc : [];
      if (bufferedArray.length === 0) return prevArray;
      return [...bufferedArray, ...prevArray];
    });

    setLoadedStateData(prev => {
      const prevArray = Array.isArray(prev) ? prev : [];
      const bufferedArray = Array.isArray(currentBuffered.ls) ? currentBuffered.ls : [];
      if (bufferedArray.length === 0) return prevArray;
      return [...bufferedArray, ...prevArray];
    });

    setUnloadedStateData(prev => {
      const prevArray = Array.isArray(prev) ? prev : [];
      const bufferedArray = Array.isArray(currentBuffered.us) ? currentBuffered.us : [];
      if (bufferedArray.length === 0) return prevArray;
      return [...bufferedArray, ...prevArray];
    });

    setOffRouteData(prev => {
      const prevArray = Array.isArray(prev) ? prev : [];
      const bufferedArray = Array.isArray(currentBuffered.or) ? currentBuffered.or : [];
      if (bufferedArray.length === 0) return prevArray;
      return [...bufferedArray, ...prevArray];
    });

    setOffAreaData(prev => {
      const prevArray = Array.isArray(prev) ? prev : [];
      const bufferedArray = Array.isArray(currentBuffered.oa) ? currentBuffered.oa : [];
      if (bufferedArray.length === 0) return prevArray;
      return [...bufferedArray, ...prevArray];
    });

    setOverSpeedData(prev => {
      const prevArray = Array.isArray(prev) ? prev : [];
      const bufferedArray = Array.isArray(currentBuffered.os) ? currentBuffered.os : [];
      if (bufferedArray.length === 0) return prevArray;
      return [...bufferedArray, ...prevArray];
    });

    setUnauthorizedStoppageData(prev => {
      const prevArray = Array.isArray(prev) ? prev : [];
      const bufferedArray = Array.isArray(currentBuffered.usp) ? currentBuffered.usp : [];
      if (bufferedArray.length === 0) return prevArray;
      return [...bufferedArray, ...prevArray];
    });

    setTamperData(prev => {
      const prevArray = Array.isArray(prev) ? prev : [];
      const bufferedArray = Array.isArray(currentBuffered.ta) ? currentBuffered.ta : [];
      if (bufferedArray.length === 0) return prevArray;
      return [...bufferedArray, ...prevArray];
    });

    setCameraTamperingData(prev => {
      const prevArray = Array.isArray(prev) ? prev : [];
      const bufferedArray = Array.isArray(currentBuffered.ct) ? currentBuffered.ct : [];
      if (bufferedArray.length === 0) return prevArray;
      return [...bufferedArray, ...prevArray];
    });

    setSafetyHazardData(prev => {
      const prevArray = Array.isArray(prev) ? prev : [];
      const bufferedArray = Array.isArray(currentBuffered.sh) ? currentBuffered.sh : [];
      if (bufferedArray.length === 0) return prevArray;
      return [...bufferedArray, ...prevArray];
    });

    setInsufficientilluminationData(prev => {
      const prevArray = Array.isArray(prev) ? prev : [];
      const bufferedArray = Array.isArray(currentBuffered.ii) ? currentBuffered.ii : [];
      if (bufferedArray.length === 0) return prevArray;
      return [...bufferedArray, ...prevArray];
    });

    setBufferedEvents({
      cd: [],
      vd: [],
      pd: [],
      vc: [],
      ls: [],
      us: [],
      ct: [],
      or: [],
      ta: [],
      sh: [],
      ii: []
    });

    setPausedTime(null);
  }, []);

  const processEventWithCameraData = useCallback((message, eventType, eventId, cameraData, location, httpsUrl, timestamp) => {
    const fullEventTypeMap = {
      cd: "Crowd Detection",
      vd: "Vehicle Detection",
      pd: "Person Detection",
      id: "Intrusion Detection",
      vc: "Traffic Congestion",
      ls: "Loaded State",
      us: "Unloaded State",
      or: "Off-Route Alert",
      ta: "Tamper Alert",
      sh: "Safety Hazard",
      ii: "Insufficient Illumination"
    };

    const newEvent = {
      id: eventId,
      eventId: eventId,
      cameraIp: message.cameraIp,
      groupId: message.groupId || cameraData.groupId,
      location: message.attributes?.location || location,
      area: message.attributes?.area || cameraData.location || "Unknown area",
      category: cameraData.category || "Unknown",
      eventType: fullEventTypeMap[eventType],
      httpsUrl: httpsUrl || null,
      timestamp,
      eventTime: message.eventTime || null,
      receivedWhilePaused: pauseStateRef.current,
      severity: message.severity || "normal",
      remarks: Array.isArray(message.remarks) ? message.remarks : [],
      imageUrl: message.imageUrl || (httpsUrl ? `${httpsUrl}/event-image/${eventId}` : null),
      videoStreamUrl: getVideoStreamUrl(message.cameraIp, message.groupId || cameraData.groupId),
      cameraInfo: {
        id: cameraData.id,
        name: cameraData.name,
        status: cameraData.status,
        latitude: cameraData.latitude,
        longitude: cameraData.longitude,
        transporter: cameraData.transporter,
        hasValidCoords: cameraData.hasValidCoords,
        formattedLatitude: cameraData.formattedLatitude,
        formattedLongitude: cameraData.formattedLongitude
      },
      originalMessage: { ...message },
      connectionInfo: {
        location,
        socketUrl: location,
        httpsUrl
      },
      priorityUpdated: false,
      priorityUpdatedAt: null,
      isLivePrioritized: false,
      hasCameraData: true
    };

    const targetEventType = (eventType === 'id' || eventType === 'pd') ? 'pd' : eventType;
    handleEventData(targetEventType, newEvent);
  }, [getVideoStreamUrl, handleEventData, pauseStateRef]);

  const processEventWithoutCameraData = useCallback((message, eventType, eventId, location, httpsUrl, timestamp) => {
    const fullEventTypeMap = {
      cd: "Crowd Detection",
      vd: "Vehicle Detection",
      pd: "Person Detection",
      id: "Intrusion Detection",
      vc: "Traffic Congestion",
      ls: "Loaded State",
      us: "Unloaded State",
      ct: "Camera Tampering",
      sh: "Safety Hazard",
      ii: "Insufficient Illumination",
    };

    const newEvent = {
      id: eventId,
      eventId: eventId,
      cameraIp: message.cameraIp,
      groupId: message.groupId,
      location: message.attributes?.location || location,
      area: message.attributes?.area || "Unknown area",
      category: "Unknown",
      eventType: fullEventTypeMap[eventType],
      httpsUrl: httpsUrl || null,
      timestamp,
      eventTime: message.eventTime || null,
      receivedWhilePaused: pauseStateRef.current,
      severity: message.severity || "normal",
      remarks: Array.isArray(message.remarks) ? message.remarks : [],
      imageUrl: message.imageUrl || (httpsUrl ? `${httpsUrl}/event-image/${eventId}` : null),
      videoStreamUrl: null,
      cameraInfo: null,
      originalMessage: { ...message },
      connectionInfo: {
        location,
        socketUrl: location,
        httpsUrl
      },
      priorityUpdated: false,
      priorityUpdatedAt: null,
      isLivePrioritized: false,
      hasCameraData: false,
      needsCameraDataRetry: true
    };

    const targetEventType = (eventType === 'id' || eventType === 'pd') ? 'pd' : eventType;
    handleEventData(targetEventType, newEvent);
  }, [handleEventData, pauseStateRef]);

  const enhancedWebSocketMessageHandler = useCallback((event, location, httpsUrl) => {
    try {
      const data = JSON.parse(event.data);

      if (data.cameras && Array.isArray(data.cameras) && data.cameras.length > 0) {
        processCameraData(data.cameras);
      }

      if (data.message) {
        const { message } = data;

        const originalEventType = message.eventType;
        const eventType = message.eventType?.toLowerCase();
        const timestamp = Date.now();

        if (!eventType || !message.cameraIp) {
          console.warn('⚠️ Invalid event message - missing required fields:', message);
          return;
        }

        if (isDuplicateEvent(message, eventType, timestamp)) {
          return;
        }

        const fullEventTypeMap = {
          cd: "Crowd Detection",
          vd: "Vehicle Detection",
          pd: "Person Detection",
          id: "Intrusion Detection",
          vc: "Traffic Congestion",
          ls: "Loaded State",
          us: "Unloaded State",
          ct: "Camera Tampering",
          or: "Off-Route Alert",
          ta: "Tamper Alert",
          sh: "Safety Hazard",
          ii: "Insufficient Illumination"
        };

        if (!fullEventTypeMap[eventType]) {
          console.warn(`⚠️ Unknown event type: ${eventType} (original: ${originalEventType})`);
          console.warn('Available event types:', Object.keys(fullEventTypeMap));
          return;
        }

        const eventId = message.eventId || generateUniqueEventId(
          message.cameraIp,
          eventType,
          timestamp,
          message
        );

        let cameraData = getCameraByIp(message.cameraIp);

        if (!cameraData) {

          setTimeout(() => {
            cameraData = getCameraByIp(message.cameraIp);

            if (cameraData) {
              processEventWithCameraData(message, eventType, eventId, cameraData, location, httpsUrl, timestamp);
            } else {
              console.warn(`⚠️ Camera still not found after retry: ${message.cameraIp}`);
              // Process event without camera data
              processEventWithoutCameraData(message, eventType, eventId, location, httpsUrl, timestamp);
            }
          }, 100);

          return;
        }

        processEventWithCameraData(message, eventType, eventId, cameraData, location, httpsUrl, timestamp);
      }

      if (data.status_change && Array.isArray(data.status_change)) {
        console.log(`📹 Received ${data.status_change.length} camera status changes from ${location}`);

      }
    } catch (error) {
      console.error(`❌ Error parsing WebSocket message from ${location}:`, error);
    }
  }, [processCameraData, isDuplicateEvent, generateUniqueEventId, getCameraByIp, processEventWithCameraData, processEventWithoutCameraData, handleEventData]);

  const deleteEvent = useCallback((typeId) => {
    console.log(`🗑️ Deleting events for type: ${typeId}`);

    switch (typeId) {
      case "cd":
        setCrowdData([]);
        setBufferedEvents(prev => ({ ...prev, cd: [] }));
        break;
      case "vd":
        setVehicleData([]);
        setBufferedEvents(prev => ({ ...prev, vd: [] }));
        break;
      case "pd":
        setPersonData(prev => prev.filter(event =>
          event.detectionType !== 'PD' &&
          event.type !== 'PD' &&
          event.originalMessage?.eventType !== 'PD'
        ));
        setBufferedEvents(prev => ({ ...prev, pd: [] }));
        break;
      case "id":
        setPersonData(prev => prev.filter(event =>
          event.detectionType !== 'ID' &&
          event.type !== 'ID' &&
          event.originalMessage?.eventType !== 'ID'
        ));
        setBufferedEvents(prev => ({ ...prev, id: [] }));
        break;
      case "vc":
        setCongestionData([]);
        setBufferedEvents(prev => ({ ...prev, vc: [] }));
        break;
      case "ls":
        setLoadedStateData([]);
        setUnloadedStateData([]);
        setBufferedEvents(prev => ({ ...prev, ls: [], us: [] }));
        break;
      case "or":
        setOffRouteData([]);
        setBufferedEvents(prev => ({ ...prev, or: [] }));
        break;
      case "ta":
        setTamperData([]);
        setBufferedEvents(prev => ({ ...prev, ta: [] }));
        break;
      case "oa":
        setOffAreaData([]);
        setBufferedEvents(prev => ({ ...prev, oa: [] }));
        break;
      case "os":
        setOverSpeedData([]);
        setBufferedEvents(prev => ({ ...prev, os: [] }));
        break;
      case "usp":
        setUnauthorizedStoppageData([]);
        setBufferedEvents(prev => ({ ...prev, usp: [] }));
        break;
      case "ct":
        setCameraTamperingData([]);
        setBufferedEvents(prev => ({ ...prev, ct: [] }));
        break;
      case "sh":
        setSafetyHazardData([]);
        setBufferedEvents(prev => ({ ...prev, sh: [] }));
        break;
      case "ii":
        setInsufficientilluminationData([]);
        setBufferedEvents(prev => ({ ...prev, ii: [] }));
        break;
      default:
        console.warn(`Unknown event type for deletion: ${typeId}`);
    }
  }, []);

  const clearBufferedEvents = useCallback(() => {
    setBufferedEvents({
      cd: [],
      vd: [],
      pd: [],
      vc: [],
      ls: [],
      us: [],
      or: [],
      ta: [],
      sh: [],
      os: [],
      usp: [],
      ii: []
    });
  }, []);

  const getBufferedEventsCount = useCallback(() => {
    const currentBuffered = bufferedEventsRef.current || {};
    return Object.values(currentBuffered).reduce(
      (total, events) => total + (Array.isArray(events) ? events.length : 0),
      0
    );
  }, []);

  const getLivePrioritizedEventsCount = useCallback(() => {
    const crowdArray = Array.isArray(crowdData) ? crowdData : [];
    const vehicleArray = Array.isArray(vehicleData) ? vehicleData : [];
    const personArray = Array.isArray(personData) ? personData : [];
    const congestionArray = Array.isArray(congestionData) ? congestionData : [];
    const loadedArray = Array.isArray(loadedStateData) ? loadedStateData : [];
    const unloadedArray = Array.isArray(unloadedStateData) ? unloadedStateData : [];
    const offRouteArray = Array.isArray(offRouteData) ? offRouteData : [];
    const tamperArray = Array.isArray(tamperData) ? tamperData : [];
    const overSpeedArray = Array.isArray(overSpeedData) ? overSpeedData : [];
    const unauthorizedStoppageArray = Array.isArray(unauthorizedStoppageData) ? unauthorizedStoppageData : [];
    const cameraTamperingArray = Array.isArray(cameraTamperingData) ? cameraTamperingData : [];
    const safetyHazardArray = Array.isArray(safetyHazardData) ? safetyHazardData : [];
    const insufficientIlluminationArray = Array.isArray(insufficientilluminationData) ? insufficientilluminationData : [];

    const allData = [...crowdArray, ...vehicleArray, ...personArray, ...congestionArray, ...loadedArray, ...unloadedArray, ...offRouteArray, ...tamperArray, ...cameraTamperingArray, ...safetyHazardArray, ...insufficientIlluminationArray];
    return allData.filter(event => event.isLivePrioritized).length;
  }, [crowdData, vehicleData, personData, congestionData, loadedStateData, unloadedStateData, offRouteData, tamperData, cameraTamperingData, safetyHazardData, insufficientilluminationData]);

  const getDataForDetectionType = useCallback((typeId, selectedArea, selectedCategory) => {
    let data = [];
    switch (typeId) {
      case "cd": data = Array.isArray(crowdData) ? crowdData : []; break;
      case "vd": data = Array.isArray(vehicleData) ? vehicleData : []; break;
      case "pd": data = Array.isArray(personData) ? personData : []; break;
      case "vc": data = Array.isArray(congestionData) ? congestionData : []; break;
      case "ls": data = Array.isArray(loadedStateData) ? loadedStateData : []; break;
      case "us": data = Array.isArray(unloadedStateData) ? unloadedStateData : []; break;
      case "or": data = Array.isArray(offRouteData) ? offRouteData : []; break;
      case "ta": data = Array.isArray(tamperData) ? tamperData : []; break;
      case "oa": data = Array.isArray(offAreaData) ? offAreaData : []; break;
      case "os": data = Array.isArray(overSpeedData) ? overSpeedData : []; break;
      case "usp": data = Array.isArray(unauthorizedStoppageData) ? unauthorizedStoppageData : []; break;
      case "ct":
        data = Array.isArray(cameraTamperingData) ? cameraTamperingData : [];
        break;
      case "sh":
        data = Array.isArray(safetyHazardData) ? safetyHazardData : [];
        break;
      case "ii":
        data = Array.isArray(insufficientilluminationData) ? insufficientilluminationData : [];
        break;
      default: data = [];
    }

    if (selectedArea !== "All") {
      const areaArray = Array.isArray(selectedArea) ? selectedArea : [selectedArea];
      data = data.filter((event) => areaArray.includes(event.area));
    }

    if (selectedCategory !== "All") {
      const categoryArray = Array.isArray(selectedCategory) ? selectedCategory : [selectedCategory];
      data = data.filter((event) => categoryArray.includes(event.category));
    }

    return data;
  }, [crowdData, vehicleData, personData, congestionData, loadedStateData, unloadedStateData, offRouteData, tamperData, offAreaData, overSpeedData, unauthorizedStoppageData, cameraTamperingData, safetyHazardData, insufficientilluminationData]);

  useEffect(() => {
    const createWebSocketConnection = ({ location, url, httpsUrl }) => {
      if (socketRefs.current[location]?.readyState === WebSocket.OPEN) {
        console.log(`🔄 Reusing existing connection for ${location}`);
        return;
      }

      if (socketRefs.current[location]) {
        socketRefs.current[location].close();
      }

      try {
        console.log(`🔌 Creating WebSocket connection for ${location}: ${url}`);
        const socket = new WebSocket(url);
        socketRefs.current[location] = socket;

        socket.onopen = () => {
          console.log(`✅ WebSocket connected for ${location}`);
          setConnectionStatus(prev => ({ ...prev, [location]: 'connected' }));
          // Reset staleness clock on (re)connect so watchdog doesn't fire immediately
          if (location === VEHICLE_ALERT_SOCKET.location) {
            vtsLastMessageRef.current = Date.now();
          }
        };

        socket.onmessage = (event) => {
          if (location === "Vehicle Alerts") {
            vtsLastMessageRef.current = Date.now(); // update staleness tracker
            handleVehicleAlertMessage(event);
          } else {
            enhancedWebSocketMessageHandler(event, location, httpsUrl);
          }
        };

        socket.onerror = (error) => {
          console.error(`❌ WebSocket error for ${location}:`, error);
          setConnectionStatus(prev => ({ ...prev, [location]: 'error' }));
        };

        socket.onclose = (event) => {
          console.log(`⚠️ WebSocket closed for ${location}: Code=${event.code}, Reason=${event.reason}`);
          setConnectionStatus(prev => ({ ...prev, [location]: 'disconnected' }));

          // Retry loop with exponential backoff (max 30 s). Skips intentional and clean closes.
          if (!socketRefs.current.intentionalClose && event.code !== 1000 && event.code !== 1001) {
            const baseDelay = event.code === 1006 ? 2000 : 5000;
            let attempt = 0;
            const scheduleReconnect = () => {
              const delay = Math.min(baseDelay * Math.pow(2, attempt), 30000);
              attempt++;
              console.log(`🔄 Reconnect attempt ${attempt} for ${location} in ${delay / 1000}s...`);
              setTimeout(() => {
                if (socketRefs.current.intentionalClose) return;
                // If socket somehow ended up OPEN (race), don't create duplicate
                if (socketRefs.current[location]?.readyState === WebSocket.OPEN) return;
                try {
                  createWebSocketConnection({ location, url, httpsUrl });
                } catch (e) {
                  console.error(`❌ Reconnect failed for ${location}:`, e);
                  scheduleReconnect(); // keep retrying
                }
              }, delay);
            };
            scheduleReconnect();
          }
        };

      } catch (error) {
        console.error(`❌ Error creating WebSocket for ${location}:`, error);
        setConnectionStatus(prev => ({ ...prev, [location]: 'error' }));
      }
    };

    // Determine which socket URLs to connect to based on user's area
    const loggedInArea = getLoggedInArea();
    const userIsAdmin = isAdminUser();

    let socketsToConnect = [];

    if (userIsAdmin) {
      // Admin: connect to all sockets
      console.log('👨‍💼 Admin user detected - connecting to all area sockets');
      socketsToConnect = [...SOCKET_URLS];
    } else if (loggedInArea) {
      // Area-wise user: connect only to their area's socket
      const areaLocation = AREA_TOKEN_TO_LOCATION[loggedInArea];
      if (areaLocation) {
        const areaSocket = SOCKET_URLS.find(s =>
          s.location === areaLocation ||
          areaLocation?.toLowerCase().includes(s.location?.toLowerCase()) ||
          s.location?.toLowerCase().includes(areaLocation?.toLowerCase())
        );
        if (areaSocket) {
          console.log(`🏢 Area-wise user detected - connecting only to ${areaLocation} socket`);
          socketsToConnect = [areaSocket];
        } else {
          console.warn(`⚠️ Could not find socket for area: ${loggedInArea}`);
          socketsToConnect = [];
        }
      } else {
        console.warn(`⚠️ Invalid logged-in area: ${loggedInArea}`);
        socketsToConnect = [];
      }
    } else {
      // No area info, default to admin behavior
      console.log('ℹ️ No area info found - connecting to all area sockets (default)');
      socketsToConnect = [...SOCKET_URLS];
    }

    // Connect to appropriate sockets
    socketsToConnect.forEach(createWebSocketConnection);

    // Always connect to vehicle alerts socket (will be filtered by area in event handler)
    createWebSocketConnection(VEHICLE_ALERT_SOCKET);

    // ── VTS keep-alive + staleness watchdog ────────────────────────────────
    // Problem: firewalls/NAT boxes on certain systems drop idle WebSocket
    // connections after 60–120 s of no traffic. The browser sees readyState
    // OPEN but no messages arrive (zombie socket).
    //
    // Two-layer fix:
    //   1. Keep-alive ping  — send a JSON ping every 30 s so the TCP
    //      connection never sits idle long enough for the firewall to kill it.
    //   2. Staleness watchdog — if no real message arrives in 90 s despite the
    //      pings (server-side issue or packet loss), force-close so onclose
    //      triggers the exponential-backoff reconnect loop.
    const VTS_PING_INTERVAL_MS   = 30000; // send ping every 30 s
    const VTS_STALE_THRESHOLD_MS = 90000; // treat as dead after 90 s of silence

    const vtsKeepalive = setInterval(() => {
      if (socketRefs.current.intentionalClose) return;
      const vtsSocket = socketRefs.current[VEHICLE_ALERT_SOCKET.location];
      if (vtsSocket && vtsSocket.readyState === WebSocket.OPEN) {
        try {
          vtsSocket.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
          console.debug('💓 VTS keep-alive ping sent');
        } catch (e) {
          console.warn('⚠️ VTS keep-alive ping failed:', e.message);
        }
      }
    }, VTS_PING_INTERVAL_MS);

    const vtsWatchdog = setInterval(() => {
      if (socketRefs.current.intentionalClose) return;
      const msSinceLast = Date.now() - vtsLastMessageRef.current;
      if (msSinceLast > VTS_STALE_THRESHOLD_MS) {
        const vtsSocket = socketRefs.current[VEHICLE_ALERT_SOCKET.location];
        if (vtsSocket && vtsSocket.readyState === WebSocket.OPEN) {
          console.warn(`🔁 VTS socket stale (${Math.round(msSinceLast / 1000)}s without message) — forcing reconnect`);
          vtsLastMessageRef.current = Date.now(); // reset so watchdog doesn't fire again immediately
          vtsSocket.close(4000, 'Stale connection');
          // onclose will trigger the exponential-backoff reconnect loop
        }
      }
    }, 30000); // check every 30 s so we catch the 90 s threshold quickly

    return () => {
      clearInterval(vtsKeepalive);
      clearInterval(vtsWatchdog);
      socketRefs.current.intentionalClose = true;
      Object.entries(socketRefs.current).forEach(([location, socket]) => {
        if (socket && typeof socket.close === 'function') {
          socket.close(1000, "Component unmounting");
        }
      });
    };
  }, []);

  // Boom Barrier WebSocket Connection
  //
  // Same "connects once, then silently drops" issue seen on the VTS socket:
  // idle firewalls/NAT boxes kill the TCP connection after ~60-120s with no
  // traffic, leaving the browser thinking readyState is still OPEN (zombie
  // socket) even though the server has stopped delivering data.
  //
  // Fix mirrors the VTS socket:
  //   1. Keep-alive ping every 30s (server already replies with 'pong').
  //   2. Staleness watchdog — force-close if nothing (including the server's
  //      own 30s heartbeat) has arrived in 90s, so onclose fires and
  //      reconnect kicks in.
  //   3. Exponential-backoff reconnect on close instead of a fixed 5s delay,
  //      and also treat close code 1001 (going away) as non-clean like VTS does.
  useEffect(() => {
    let boomReconnectAttempt = 0;

    const createBoomBarrierWebSocketConnection = () => {
      if (boomBarrierSocketRef.current?.readyState === WebSocket.OPEN ||
        boomBarrierSocketRef.current?.readyState === WebSocket.CONNECTING) {
        return;
      }

      try {
        console.log(`🚧 Connecting to Boom Barrier WebSocket: ${BOOM_BARRIER_SOCKET_URL}`);
        const socket = new WebSocket(BOOM_BARRIER_SOCKET_URL);
        boomBarrierSocketRef.current = socket;

        socket.onopen = () => {
          console.log(`✅ Boom Barrier WebSocket connected`);
          setConnectionStatus(prev => ({ ...prev, boomBarrier: 'connected' }));
          boomReconnectAttempt = 0; // reset backoff on a healthy connection
          boomLastMessageRef.current = Date.now(); // reset staleness clock on (re)connect
        };

        socket.onmessage = (event) => {
          boomLastMessageRef.current = Date.now(); // any traffic (incl. heartbeat/pong) counts

          try {
            const data = JSON.parse(event.data);

            if (data.type === 'boom_barrier_alerts') {
              console.log(`🚧 Received boom barrier update: ${data.data?.length || 0} active alerts`);
              setBoomBarrierData(data.data || []);

              if (data.data && data.data.length > 0) {
                console.log('⚠️ Active boom alerts:', data.data);
              } else {
                console.log('✅ All booms closed');
              }
            } else if (data.type === 'heartbeat') {
              console.log('💓 Boom barrier heartbeat');
            } else if (data.type === 'pong') {
              console.debug('💓 Boom barrier keep-alive pong received');
            }
          } catch (error) {
            console.error(`❌ Error parsing boom barrier message:`, error);
          }
        };

        socket.onerror = (error) => {
          console.error(`❌ Boom Barrier WebSocket error:`, error);
          setConnectionStatus(prev => ({ ...prev, boomBarrier: 'error' }));
        };

        socket.onclose = (event) => {
          console.log(`⚠️ Boom Barrier WebSocket closed: Code=${event.code}`);
          setConnectionStatus(prev => ({ ...prev, boomBarrier: 'disconnected' }));

          if (!boomBarrierSocketRef.current?.intentionalClose && event.code !== 1000 && event.code !== 1001) {
            const baseDelay = event.code === 1006 ? 2000 : 5000;
            const delay = Math.min(baseDelay * Math.pow(2, boomReconnectAttempt), 30000);
            boomReconnectAttempt++;
            console.log(`🔄 Reconnect attempt ${boomReconnectAttempt} for Boom Barrier socket in ${delay / 1000}s...`);
            setTimeout(() => {
              if (boomBarrierSocketRef.current?.intentionalClose) return;
              if (boomBarrierSocketRef.current?.readyState === WebSocket.OPEN) return;
              createBoomBarrierWebSocketConnection();
            }, delay);
          }
        };

      } catch (error) {
        console.error(`❌ Error creating Boom Barrier WebSocket:`, error);
      }
    };

    createBoomBarrierWebSocketConnection();

    const BOOM_PING_INTERVAL_MS   = 30000; // send ping every 30 s
    const BOOM_STALE_THRESHOLD_MS = 90000; // treat as dead after 90 s of silence

    const boomKeepalive = setInterval(() => {
      if (boomBarrierSocketRef.current?.intentionalClose) return;
      const socket = boomBarrierSocketRef.current;
      if (socket && socket.readyState === WebSocket.OPEN) {
        try {
          socket.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
          console.debug('💓 Boom barrier keep-alive ping sent');
        } catch (e) {
          console.warn('⚠️ Boom barrier keep-alive ping failed:', e.message);
        }
      }
    }, BOOM_PING_INTERVAL_MS);

    const boomWatchdog = setInterval(() => {
      if (boomBarrierSocketRef.current?.intentionalClose) return;
      const msSinceLast = Date.now() - boomLastMessageRef.current;
      if (msSinceLast > BOOM_STALE_THRESHOLD_MS) {
        const socket = boomBarrierSocketRef.current;
        if (socket && socket.readyState === WebSocket.OPEN) {
          console.warn(`🔁 Boom barrier socket stale (${Math.round(msSinceLast / 1000)}s without message) — forcing reconnect`);
          boomLastMessageRef.current = Date.now(); // reset so watchdog doesn't fire again immediately
          socket.close(4000, 'Stale connection');
          // onclose will trigger the exponential-backoff reconnect loop
        }
      }
    }, 30000); // check every 30 s so we catch the 90 s threshold quickly

    return () => {
      clearInterval(boomKeepalive);
      clearInterval(boomWatchdog);
      if (boomBarrierSocketRef.current) {
        boomBarrierSocketRef.current.intentionalClose = true;
        if (boomBarrierSocketRef.current.readyState === WebSocket.OPEN) {
          boomBarrierSocketRef.current.close(1000, "Component unmounting");
        }
        boomBarrierSocketRef.current = null;
      }
    };
  }, []);

  const debugPriorityMap = useCallback(() => {
    console.log('🔍 Priority Map Debug:', {
      totalMappedEvents: eventPriorityMap.current.size,
      crowdEvents: crowdData.length,
      vehicleEvents: vehicleData.length,
      personEvents: personData.length,
      congestionEvents: congestionData.length,
      loadedStateEvents: loadedStateData.length,
      unloadedStateEvents: unloadedStateData.length,
      offRouteEvents: offRouteData.length,
      tamperEvents: tamperData.length,
      cameraTamperingEvents: cameraTamperingData.length,
      safetyHazardEvents: safetyHazardData.length,
      insufficientIlluminationEvents: insufficientilluminationData.length,
      bufferCounts: getBufferedEventsCount(),
      livePrioritizedTotal: getLivePrioritizedEventsCount(),
      cameraDataCount: Object.keys(camerasData).length,
      cameraGroupsCount: Object.keys(camerasByGroupId).length
    });
  }, [crowdData.length, vehicleData.length, personData.length, congestionData.length, loadedStateData.length, unloadedStateData.length, offRouteData.length, tamperData.length,
    cameraTamperingData.length, safetyHazardData.length, insufficientilluminationData.length,
    getBufferedEventsCount, getLivePrioritizedEventsCount, camerasData, camerasByGroupId]);

  return {
    connectionStatus,
    crowdData,
    vehicleData,
    personData,
    congestionData,
    loadedStateData,
    unloadedStateData,
    offRouteData,
    tamperData,
    offAreaData,
    overSpeedData,
    unauthorizedStoppageData,
    cameraTamperingData,
    safetyHazardData,
    insufficientilluminationData,
    boomBarrierData,
    deleteEvent,
    getDataForDetectionType,
    isPaused,
    togglePause,
    pausedTime,
    bufferedEvents,
    getBufferedEventsCount,
    clearBufferedEvents,

    updateEventPriority,
    getEventPriority,
    getLivePrioritizedEventsCount,
    debugPriorityMap,
    camerasData,
    camerasByIp,
    camerasByGroupId,
    getCameraByIp,
    getCameraById,
    getCamerasByGroupId,
    getVideoStreamUrl,
    getVideoStreamUrlByEvent,
    isCameraOnline,
    getAllCamerasWithStreams,
    getCameraStats
  };
};

export default useWebSockets;